import "../../assets/css/login.css";
import logo from "../../assets/images/logo.png";
import axios from "axios";
import { FORGOT_PASSWORD, FORGOT_OTP_VERIFY, RESET_PASSWORD } from "../../routes/routes";
import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ValidationMessage } from "../../components/Utils";

/**
 * 6-Box OTP Input with '-' placeholder, auto-focus & paste support
 */
const OtpInput = ({ length = 6, value = "", onChange, hasError }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleChange = (index, e) => {
    const rawVal = e.target.value;
    const cleanNumbers = rawVal.replace(/\D/g, "");

    if (!cleanNumbers) {
      const newDigits = [...digits];
      newDigits[index] = "";
      onChange(newDigits.join(""));
      return;
    }

    if (cleanNumbers.length > 1) {
      const pastedChars = cleanNumbers.slice(0, length).split("");
      const newDigits = [...digits];
      for (let i = 0; i < length; i++) {
        if (i >= index && pastedChars[i - index] !== undefined) {
          newDigits[i] = pastedChars[i - index];
        }
      }
      const finalOtp = newDigits.join("");
      onChange(finalOtp);
      const nextFocus = Math.min(index + pastedChars.length, length - 1);
      inputRefs.current[nextFocus]?.focus();
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = cleanNumbers;
    onChange(newDigits.join(""));

    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pastedData) return;

    onChange(pastedData);
    const targetIdx = Math.min(pastedData.length, length - 1);
    inputRefs.current[targetIdx]?.focus();
  };

  return (
    <div className="otp-boxes-container" onPaste={handlePaste}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (inputRefs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          placeholder="-"
          className={`otp-box-item ${digit ? "is-filled" : ""} ${hasError ? "has-error" : ""}`}
          value={digit}
          autoFocus={idx === 0}
          onFocus={(e) => e.target.select()}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  // Step 1: 'email', Step 2: 'otp', Step 3: 'reset'
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [type, setType] = useState("password");
  const [confirmType, setConfirmType] = useState("password");

  const [btnDisabled, setBtnDisabled] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * Step 1: Request OTP for Email
   */
  const handleSendOtp = (e) => {
    e.preventDefault();
    setErrors({});
    if (!email.trim()) {
      const msg = "Please enter your email address";
      setErrors({ email: [msg] });
      toast.error(msg);
      return;
    }

    setBtnDisabled(true);
    axios
      .post(FORGOT_PASSWORD, { email })
      .then((response) => {
        if (response?.data?.success || response?.status === 200) {
          toast.success(response?.data?.msg || "OTP sent successfully to your registered email!");
          setStep(2);
        }
      })
      .catch((error) => {
        const errorMsg = error?.response?.data?.msg || "Something went wrong. Please check your email.";
        if (error?.response?.data?.error && Object.keys(error.response.data.error).length > 0) {
          setErrors(error.response.data.error);
        } else {
          setErrors({ email: [errorMsg] });
        }
        toast.error(errorMsg);
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  /**
   * Step 2: Verify OTP
   */
  const handleVerifyOtp = (e) => {
    e.preventDefault();
    setErrors({});
    if (!otp.trim() || otp.trim().length < 6) {
      const msg = "Please enter the complete 6-digit OTP";
      setErrors({ otp: [msg] });
      toast.error(msg);
      return;
    }

    setBtnDisabled(true);
    axios
      .post(FORGOT_OTP_VERIFY, { email, otp: Number(otp) })
      .then((response) => {
        if (response?.data?.success || response?.status === 200) {
          toast.success(response?.data?.msg || "OTP verified successfully!");
          setOtp("");
          setStep(3);
        }
      })
      .catch((error) => {
        const errorMsg = error?.response?.data?.msg || "Invalid or expired OTP";
        if (error?.response?.data?.error && Object.keys(error.response.data.error).length > 0) {
          setErrors(error.response.data.error);
        } else {
          setErrors({ otp: [errorMsg] });
        }
        toast.error(errorMsg);
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  /**
   * Step 3: Reset Password
   */
  const handleResetPassword = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!password) {
      newErrors.password = ["Password is required"];
    } else if (password.length < 6) {
      newErrors.password = ["Password must be at least 6 characters"];
    }

    if (!confirmPassword) {
      newErrors.confirm_password = ["Confirm password is required"];
    } else if (password !== confirmPassword) {
      newErrors.confirm_password = ["Passwords do not match"];
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Toast first validation error
      const firstError = newErrors.password?.[0] || newErrors.confirm_password?.[0];
      if (firstError) {
        toast.error(firstError);
      }
      return;
    }

    setBtnDisabled(true);
    axios
      .post(RESET_PASSWORD, { email, password })
      .then((response) => {
        if (response?.data?.success || response?.status === 200) {
          toast.success(response?.data?.msg || "Password reset successfully!");
          navigate("/login");
        }
      })
      .catch((error) => {
        const errorMsg = error?.response?.data?.msg || "Failed to reset password. Please try again.";
        if (error?.response?.data?.error && Object.keys(error.response.data.error).length > 0) {
          setErrors(error.response.data.error);
        } else {
          setErrors({ password: [errorMsg] });
        }
        toast.error(errorMsg);
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  /**
   * Resend OTP handler
   */
  const handleResendOtp = () => {
    setBtnDisabled(true);
    axios
      .post(FORGOT_PASSWORD, { email })
      .then((response) => {
        toast.success(response?.data?.msg || "New OTP sent to your email!");
        setOtp("");
      })
      .catch((error) => {
        toast.error(error?.response?.data?.msg || "Unable to resend OTP at this time.");
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="login-pg">
        {/* ── Left Hero Panel ─────────────────── */}
        <div className="col-lg-7 bg-wrappers d-none d-lg-block p-0">
          <div className="login-bg">
            <div className="login-bg-shapes" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>

            <div className="login-hero-content">
              <h2>
                Connecting Business.
                <br />
                Creating Opportunities.
              </h2>
              <p>
                Recover access to your Iraaya World account quickly and securely.
              </p>
              <div className="login-stats">
                <div className="login-stat-pill">
                  <i className="bx bx-shield-quarter"></i>
                  Safe &amp; Secure
                </div>
                <div className="login-stat-pill">
                  <i className="bx bx-check-shield"></i>
                  Instant Verification
                </div>
                <div className="login-stat-pill">
                  <i className="bx bx-key"></i>
                  Quick Password Reset
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Form Panel ─────────────────── */}
        <div className="col-lg-5 col-md-12 col-sm-12 p-0">
          <div className="login-wrappers">
            <div className="login-container">
              {/* Logo */}
              <div className="login-content">
                <Link to="/login">
                  <img
                    src={logo}
                    className="login-logo-one"
                    alt="Iraaya World Logo"
                  />
                </Link>
              </div>

              {/* Steps Progress Indicator */}
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  <span
                    onClick={() => setStep(1)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: step >= 1 ? "#CF2030" : "#e2e8f0",
                      color: step >= 1 ? "#fff" : "#64748b",
                      cursor: "pointer",
                    }}
                    title="Go to Email Step"
                  >
                    1
                  </span>
                  <span
                    onClick={() => setStep(1)}
                    style={{ color: step >= 1 ? "#CF2030" : "#94a3b8", cursor: "pointer" }}
                  >
                    Email
                  </span>
                  <span style={{ color: "#cbd5e1" }}>——</span>

                  <span
                    onClick={() => step >= 2 && setStep(2)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: step >= 2 ? "#CF2030" : "#e2e8f0",
                      color: step >= 2 ? "#fff" : "#64748b",
                      cursor: step >= 2 ? "pointer" : "default",
                    }}
                    title={step >= 2 ? "Go to OTP Step" : ""}
                  >
                    2
                  </span>
                  <span
                    onClick={() => step >= 2 && setStep(2)}
                    style={{ color: step >= 2 ? "#CF2030" : "#94a3b8", cursor: step >= 2 ? "pointer" : "default" }}
                  >
                    OTP
                  </span>
                  <span style={{ color: "#cbd5e1" }}>——</span>

                  <span
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: step >= 3 ? "#CF2030" : "#e2e8f0",
                      color: step >= 3 ? "#fff" : "#64748b",
                    }}
                  >
                    3
                  </span>
                  <span style={{ color: step >= 3 ? "#CF2030" : "#94a3b8" }}>Reset</span>
                </div>
              </div>

              {/* Divider Heading */}
              <div className="login-divider">
                <span></span>
                <small>
                  {step === 1 && "Forgot Password"}
                  {step === 2 && "Enter Verification Code"}
                  {step === 3 && "Set New Password"}
                </small>
                <span></span>
              </div>

              {/* STEP 1: EMAIL FORM */}
              {step === 1 && (
                <form
                  id="formForgotPassword"
                  className="mb-3"
                  onSubmit={handleSendOtp}
                  autoComplete="off"
                >
                  <p className="text-muted text-center mb-3" style={{ fontSize: "0.85rem" }}>
                    Enter your registered email address and we'll send you an OTP to reset your password.
                  </p>

                  <div className="login-field-group">
                    <label htmlFor="email" className="theme-label">
                      Email Address
                    </label>
                    <div className="login-input-wrap">
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={email}
                        placeholder="you@example.com"
                        autoFocus
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <i className="bx bx-envelope input-icon"></i>
                    </div>
                    <ValidationMessage error={errors} name="email" />
                  </div>

                  <div className="mt-4 mb-3">
                    <button
                      disabled={btnDisabled}
                      className="btn btn-primary login-submit d-grid w-100"
                      type="submit"
                    >
                      {btnDisabled ? "Sending OTP…" : "Send OTP"}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 2: OTP FORM */}
              {step === 2 && (
                <form
                  id="formVerifyOtp"
                  className="mb-3"
                  onSubmit={handleVerifyOtp}
                  autoComplete="off"
                >
                  <p className="text-muted text-center mb-3" style={{ fontSize: "0.85rem" }}>
                    We sent a 6-digit verification code to <strong style={{ color: "#1e293b" }}>{email}</strong>. Please enter it below.
                  </p>

                  <div className="login-field-group text-center">
                    <label className="theme-label d-block text-center mb-1">
                      Enter 6-Digit OTP Code
                    </label>
                    <OtpInput
                      length={6}
                      value={otp}
                      onChange={(val) => {
                        setOtp(val);
                        if (errors?.otp) {
                          setErrors((prev) => {
                            const newErr = { ...prev };
                            delete newErr.otp;
                            return newErr;
                          });
                        }
                      }}
                      hasError={Boolean(errors?.otp)}
                    />
                    <ValidationMessage error={errors} name="otp" />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      style={{ fontSize: "0.82rem", textDecoration: "none" }}
                      onClick={() => setStep(1)}
                    >
                      <i className="bx bx-arrow-back me-1"></i>Change Email
                    </button>
                    <button
                      type="button"
                      disabled={btnDisabled}
                      className="btn btn-link p-0"
                      style={{ fontSize: "0.82rem", color: "#CF2030", textDecoration: "none", fontWeight: "600" }}
                      onClick={handleResendOtp}
                    >
                      Resend OTP
                    </button>
                  </div>

                  <div className="mt-4 mb-3">
                    <button
                      disabled={btnDisabled}
                      className="btn btn-primary login-submit d-grid w-100"
                      type="submit"
                    >
                      {btnDisabled ? "Verifying…" : "Verify OTP"}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 3: RESET PASSWORD FORM */}
              {step === 3 && (
                <form
                  id="formResetPassword"
                  className="mb-3"
                  onSubmit={handleResetPassword}
                  autoComplete="off"
                >
                  <p className="text-muted text-center mb-3" style={{ fontSize: "0.85rem" }}>
                    Create a new, strong password for your account.
                  </p>

                  {/* New Password */}
                  <div className="login-field-group form-password-toggle">
                    <label className="theme-label" htmlFor="new_password">
                      New Password
                    </label>
                    <div className="login-input-wrap input-group input-group-merge">
                      <input
                        type={type}
                        id="new_password"
                        className="form-control"
                        name="password"
                        placeholder="At least 6 characters"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <i className="bx bx-lock-alt input-icon"></i>
                      <span
                        className="input-group-text cursor-pointer"
                        onClick={() => setType(type === "password" ? "text" : "password")}
                        title="Toggle password"
                      >
                        <i className={`bx ${type === "password" ? "bx-hide" : "bx-show"}`}></i>
                      </span>
                    </div>
                    <ValidationMessage error={errors} name="password" />
                  </div>

                  {/* Confirm Password */}
                  <div className="login-field-group form-password-toggle mt-3">
                    <label className="theme-label" htmlFor="confirm_password">
                      Confirm New Password
                    </label>
                    <div className="login-input-wrap input-group input-group-merge">
                      <input
                        type={confirmType}
                        id="confirm_password"
                        className="form-control"
                        name="confirm_password"
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <i className="bx bx-lock-check input-icon"></i>
                      <span
                        className="input-group-text cursor-pointer"
                        onClick={() => setConfirmType(confirmType === "password" ? "text" : "password")}
                        title="Toggle password"
                      >
                        <i className={`bx ${confirmType === "password" ? "bx-hide" : "bx-show"}`}></i>
                      </span>
                    </div>
                    <ValidationMessage error={errors} name="confirm_password" />
                  </div>

                  <div className="d-flex justify-content-start align-items-center mt-3 mb-2">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      style={{ fontSize: "0.82rem", textDecoration: "none" }}
                      onClick={() => setStep(2)}
                    >
                      <i className="bx bx-arrow-back me-1"></i>Back to OTP Verification
                    </button>
                  </div>

                  <div className="mt-3 mb-3">
                    <button
                      disabled={btnDisabled}
                      className="btn btn-primary login-submit d-grid w-100"
                      type="submit"
                    >
                      {btnDisabled ? "Updating Password…" : "Reset Password"}
                    </button>
                  </div>
                </form>
              )}

              {/* Back to Login Link */}
              <div className="text-center mt-3">
                <Link
                  to="/login"
                  className="d-flex align-items-center justify-content-center gap-1"
                  style={{ color: "#64748b", textDecoration: "none", fontSize: "0.85rem", fontWeight: "500" }}
                >
                  <i className="bx bx-chevron-left" style={{ fontSize: "1.2rem" }}></i>
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
